import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Eye, Tag, Clock, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Logo from '@/components/Logo';
import SEOHead from '@/components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Blog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');

  useEffect(() => {
    loadPosts();
    loadTags();
  }, [page, selectedTag]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 9 });
      if (selectedTag) params.append('tag', selectedTag);
      
      const res = await axios.get(`${API_URL}/api/blog/posts?${params}`);
      setPosts(res.data.posts || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/blog/tags`);
      setTags(res.data.tags || []);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title="Blog - AI Insights & News | theailedger.ai"
        description="Stay updated with the latest AI trends, product guides, and industry insights from theailedger.ai"
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Marketplace
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#00F0FF]/5 to-transparent">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">AI Insights & News</h1>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
            Stay ahead of the curve with our latest articles on AI trends, product guides, and industry insights.
          </p>
        </div>
      </section>

      <main className="container mx-auto px-6 md:px-12 py-12">
        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => { setSelectedTag(''); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                !selectedTag
                  ? 'bg-[#00F0FF] text-black'
                  : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
              }`}
            >
              All Posts
            </button>
            {tags.map((tag) => (
              <button
                key={tag.name}
                onClick={() => { setSelectedTag(tag.name); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedTag === tag.name
                    ? 'bg-[#00F0FF] text-black'
                    : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                }`}
              >
                {tag.name} ({tag.count})
              </button>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00F0FF]"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-xl font-bold mb-2">No posts yet</h2>
            <p className="text-neutral-400">Check back soon for new content!</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article
                  key={post.post_id}
                  className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-[#00F0FF]/30 transition-all cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  {post.cover_image && (
                    <div className="aspect-video bg-neutral-800">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags?.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-[#00F0FF]/10 text-[#00F0FF] text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-xl font-bold mb-2 group-hover:text-[#00F0FF] transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-neutral-400 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.views || 0} views
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="border-white/20"
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-neutral-400">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  className="border-white/20"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default Blog;
